<h1 mat-dialog-title class="dialog-title"
  [class.urgent-text]="!uploadLoading && (vaccinationDue || hasMissingDetails())">
  @if (uploadLoading) {
  Updating your details
  }
  @else if (vaccinationDue) { Vaccination Reminder }
  @else if (hasMissingDetails()) { Update Your Details }
  @else { Upload vaccination records }
</h1>

@if (displayMessage && !uploadLoading) {
<mat-dialog-content class="intro-message">
  <div [innerHtml]="displayMessage"></div>
</mat-dialog-content>
}

@if (uploadLoading) {
<mat-dialog-content class="form-loader">
  <mat-spinner [diameter]="60" aria-label="Uploading files"></mat-spinner>
</mat-dialog-content>
}

<!-- Error Message -->
@if (uploadError && !uploadLoading) {
<mat-dialog-content>
  <div class="error-message" role="alert" aria-live="polite">
    <p class="error-text">{{ uploadError }}</p>
  </div>
</mat-dialog-content>
}

<!-- Form Content -->
@if (!uploadLoading) {
<mat-dialog-content class="form-container">
  @if (visibleDogs().length > 0) { @for (dog of visibleDogs(); track dog.id; let last = $last) {
  <div class="vax-section">
    <h2 class="dog-name">{{ dog.name }}</h2>

    @if (dogsNeedingVaxUpload.has(dog.id)) {
    <input [id]="'file-input-' + dog.id" class="file-input-hidden" type="file" #fileInput
      accept="image/*,.pdf,image/heic,image/heif" (change)="onFileSelected($event, dog.id)" multiple
      [attr.aria-label]="'Upload vaccination file(s) for ' + dog.name" />
    <label mat-raised-button class="upload-button" [attr.for]="'file-input-' + dog.id">
      Upload vaccination file(s) for {{ dog.name }}
    </label>

    @if (dogFormData.get(dog.id)?.files; as files) {
    <div class="files-list">
      @for (file of files; track file.name) {
      <div class="file-item">
        <span class="file-name" [title]="file.name">{{ file.name }}</span>
        <button mat-raised-button (click)="onRemoveFile($event, file, dog.id)" type="button"
          [attr.aria-label]="'Remove ' + file.name">
          Remove
        </button>
      </div>
      }
    </div>
    }
    }

    @if (dogsNeedingVaxUpload.has(dog.id) && dogDetailsForms.has(dog.id)) {
    <hr class="divider" />
    }

    @if (dogDetailsForms.get(dog.id); as detailsForm) {
    <div [formGroup]="detailsForm" class="missing-details-section">
      @if (detailsForm.contains('chip_number')) {
      <mat-form-field appearance="outline" floatLabel="always" class="details-field login-form_input login-form-field">
        <mat-label>Microchip number</mat-label>
        <input matInput formControlName="chip_number" />
      </mat-form-field>
      @if (detailsForm.get('chip_number').invalid && detailsForm.get('chip_number').touched) {
      <mat-error>Microchip number must be at least 10 characters.</mat-error>
      }
      }

      @if (detailsForm.contains('vet_detail_id')) {
      <mat-form-field appearance="outline" floatLabel="always" class="details-field">
        <mat-label>Vet</mat-label>
        <mat-select formControlName="vet_detail_id" (openedChange)="onVetSelectOpenedChange($event)">
          <mat-option>
            <ngx-mat-select-search [formControl]="vetFilterCtrl" placeholderLabel="Search vets..."
              noEntriesFoundLabel="No vets found"></ngx-mat-select-search>
          </mat-option>
          @for (vet of filteredVets | async; track vet.id) {
          <mat-option [value]="vet.id">{{ vet.name }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
      <button mat-button type="button" color="primary" class="cant-find-vet-btn" (click)="openVetNotFoundDialog()">Can't
        find your vet?</button>
      }

      @if (detailsForm.contains('worming_treatment_date')) {
      <mat-form-field appearance="outline" floatLabel="always" class="details-field sleep-over-textarea">
        <mat-label>Worming treatment date</mat-label>
        <input matInput readonly [matDatepicker]="wormingTreatmentDatePicker" [min]="treatmentMinDate"
          formControlName="worming_treatment_date" />
        <mat-datepicker-toggle matSuffix [for]="wormingTreatmentDatePicker"></mat-datepicker-toggle>
        <mat-datepicker #wormingTreatmentDatePicker></mat-datepicker>
      </mat-form-field>
      <mat-form-field appearance="outline" floatLabel="always" class="details-field">
        <mat-label>Worming frequency</mat-label>
        <mat-select formControlName="worming_treatment_frequency">
          @for (months of wormingTreatmentFrequencyOptions; track months) {
          <mat-option [value]="months">{{ months === 1 ? 'Monthly' : 'Every ' + months + ' months' }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
      @if (detailsForm.errors?.worming_treatment_dateInPast) {
      <mat-error>Treatment date cannot be in the past.</mat-error>
      }
      @if (detailsForm.errors?.worming_treatment_dateTooFarInFuture) {
      <mat-error>Treatment date cannot be further out than the selected frequency.</mat-error>
      }
      }

      @if (detailsForm.contains('flea_treatment_date')) {
      <mat-form-field appearance="outline" floatLabel="always" class="details-field sleep-over-textarea">
        <mat-label>Flea treatment date</mat-label>
        <input matInput readonly [matDatepicker]="fleaTreatmentDatePicker" [min]="treatmentMinDate"
          formControlName="flea_treatment_date" />
        <mat-datepicker-toggle matSuffix [for]="fleaTreatmentDatePicker"></mat-datepicker-toggle>
        <mat-datepicker #fleaTreatmentDatePicker></mat-datepicker>
      </mat-form-field>
      <mat-form-field appearance="outline" floatLabel="always" class="details-field">
        <mat-label>Flea frequency</mat-label>
        <mat-select formControlName="flea_treatment_frequency">
          @for (months of fleaTreatmentFrequencyOptions; track months) {
          <mat-option [value]="months">{{ months === 1 ? 'Monthly' : 'Every ' + months + ' months' }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
      @if (detailsForm.errors?.flea_treatment_dateInPast) {
      <mat-error>Treatment date cannot be in the past.</mat-error>
      }
      @if (detailsForm.errors?.flea_treatment_dateTooFarInFuture) {
      <mat-error>Treatment date cannot be further out than the selected frequency.</mat-error>
      }
      }
    </div>
    }
  </div>

  @if (!last && visibleDogs().length > 1) {
  <hr class="divider" />
  } } }

  @if (altContactForm) {
  <hr class="divider" />
  <div [formGroup]="altContactForm" class="missing-details-section">
    <h2 class="dog-name">Alternative contact</h2>
    @if (altContactForm.contains('alt_contact_firstname')) {
    <mat-form-field appearance="outline" floatLabel="always" hideRequiredMarker="true"
      class="details-field login-form_input login-form-field">
      <mat-label>Alternative contact name</mat-label>
      <input matInput formControlName="alt_contact_firstname" />
    </mat-form-field>
    @if (altContactForm.controls['alt_contact_firstname'].errors?.required &&
    altContactForm.controls['alt_contact_firstname'].touched) {
    <mat-error>Alternative contact name cannot be empty.</mat-error>
    }
    @if (altContactForm.controls['alt_contact_firstname'].errors?.maxlength) {
    <mat-error>Alternative contact name cannot be more than 100 characters.</mat-error>
    }
    @if (altContactForm.controls['alt_contact_firstname'].errors?.htmlExists) {
    <mat-error>This input contains invalid characters.</mat-error>
    }
    }
    @if (altContactForm.contains('alt_contact_number')) {
    <mat-form-field appearance="outline" floatLabel="always" class="details-field login-form_input login-form-field">
      <mat-label>Alternative contact phone number</mat-label>
      <input matInput formControlName="alt_contact_number" />
    </mat-form-field>
    @if (altContactForm.controls['alt_contact_number'].errors?.pattern) {
    <mat-error>Phone number can only contain numbers, spaces, and an optional leading +.</mat-error>
    }
    @if (altContactForm.controls['alt_contact_number'].errors?.maxlength) {
    <mat-error>Phone number cannot be more than 20 characters.</mat-error>
    }
    @if (altContactForm.controls['alt_contact_number'].errors?.htmlExists) {
    <mat-error>This input contains invalid characters.</mat-error>
    }
    @if (altContactForm.controls['alt_contact_number'].errors?.sameContactNumber) {
    <mat-error>Alternative phone number cannot be the same as your phone number.</mat-error>
    }
    }
  </div>
  }
</mat-dialog-content>
} @if (!uploadLoading) {
<mat-dialog-actions>
  <button mat-raised-button color="primary" class="submit-button" [disabled]="disableUploadButton || uploadLoading"
    (click)="onSubmit($event)" type="button" aria-label="Update vaccination records and details">
    Update
  </button>
  <button mat-raised-button mat-dialog-close type="button" class="close-button" aria-label="Close dialog">Close</button>
</mat-dialog-actions>
}